import { AquaApi, AquaApiError } from './api'
import { el, clear } from './dom'
import { STYLES } from './styles'
import { AquafyTab } from './tabs/aquafy'
import { VerifyTab } from './tabs/verify'

export type Mode = 'both' | 'aquafy' | 'verify'

export interface AquaWidgetOptions {
  apiBase: string
  mode?: Mode
  template?: string
}

export interface AquaWidgetInstance {
  on(event: 'aquafied' | 'verified' | 'error', cb: (detail: unknown) => void): void
  destroy(): void
}

/** Shared render logic for both the custom element and the programmatic mount(). */
class WidgetView {
  private listeners: Record<string, ((d: unknown) => void)[]> = {}

  constructor(private host: ShadowRoot | HTMLElement, private opts: AquaWidgetOptions) {
    this.render()
  }

  on(event: string, cb: (d: unknown) => void) {
    ;(this.listeners[event] ||= []).push(cb)
  }

  private emit(event: string, detail: unknown) {
    ;(this.listeners[event] || []).forEach((cb) => cb(detail))
  }

  private render() {
    const api = new AquaApi(this.opts.apiBase)
    const mode: Mode = this.opts.mode ?? 'both'
    const onError = (e: AquaApiError) => this.emit('error', e)
    const aquafy = new AquafyTab({
      api,
      defaultTemplate: this.opts.template,
      onAquafied: (d) => this.emit('aquafied', d),
      onError,
    })
    const verify = new VerifyTab({
      api,
      onVerified: (d) => this.emit('verified', d),
      onError,
    })

    const body = el('div', {})
    const card = el('div', { class: 'card' })

    if (mode === 'both') {
      const aquafyTab = el('button', { class: 'tab', role: 'tab', 'aria-selected': 'true' }, 'Aquafy')
      const verifyTab = el('button', { class: 'tab', role: 'tab', 'aria-selected': 'false' }, 'Verify')

      const select = (which: 'aquafy' | 'verify') => {
        aquafyTab.setAttribute('aria-selected', String(which === 'aquafy'))
        verifyTab.setAttribute('aria-selected', String(which === 'verify'))
        clear(body)
        body.append(which === 'aquafy' ? aquafy.root : verify.root)
      }

      aquafyTab.addEventListener('click', () => select('aquafy'))
      verifyTab.addEventListener('click', () => select('verify'))
      card.append(el('div', { class: 'tabs', role: 'tablist' }, aquafyTab, verifyTab), body)
      select('aquafy')
    } else {
      card.append(body)
      body.append(mode === 'verify' ? verify.root : aquafy.root)
    }

    if (this.host instanceof ShadowRoot) {
      const style = document.createElement('style')
      style.textContent = STYLES
      this.host.append(style, card)
    } else {
      this.host.append(card)
    }
  }
}

export class AquaWidgetElement extends HTMLElement {
  private view?: WidgetView

  connectedCallback() {
    const shadow = this.attachShadow({ mode: 'open' })
    const apiBase = this.getAttribute('api-base') || ''
    const mode = (this.getAttribute('mode') as Mode) || 'both'
    const template = this.getAttribute('template') || undefined

    if (!apiBase) {
      const errDiv = document.createElement('div')
      errDiv.className = 'err'
      errDiv.textContent = 'aqua-widget: missing api-base attribute'
      const style = document.createElement('style')
      style.textContent = STYLES
      shadow.append(style, errDiv)
      return
    }

    this.view = new WidgetView(shadow, { apiBase, mode, template })
    this.view.on('aquafied', (d) =>
      this.dispatchEvent(new CustomEvent('aqua:aquafied', { detail: d, bubbles: true })),
    )
    this.view.on('verified', (d) =>
      this.dispatchEvent(new CustomEvent('aqua:verified', { detail: d, bubbles: true })),
    )
    this.view.on('error', (d) =>
      this.dispatchEvent(new CustomEvent('aqua:error', { detail: d, bubbles: true })),
    )
  }
}

/** Programmatic mount for npm consumers (no custom element needed). */
export function mount(target: HTMLElement, opts: AquaWidgetOptions): AquaWidgetInstance {
  const view = new WidgetView(target, opts)
  return {
    on: (event, cb) => view.on(event, cb),
    destroy: () => {
      while (target.firstChild) target.removeChild(target.firstChild)
    },
  }
}
